import parse from 'html-react-parser'
import { DateTime } from 'luxon'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { NumberParam, useQueryParams as useParamsFromQuery } from 'use-query-params'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import BlankStateImage from '@assets/images/blank_state_placeholder.jpg'
import ConfirmPopup from '@components/ConfirmPopup'
import CustomScrollbar from '@components/CustomScrollbar'
import ErrorPopup, { ErrorPopupProps } from '@components/ErrorPopup'
import NoticePopover from '@components/NoticePopover'
import TabsCarousel from '@components/TabsCarousel'
import { isArray } from '@craco/craco/dist/lib/utils'
import alertsApi from '@modules/alerts/api'
import ProfileAlertPopover from '@modules/alerts/components/ProfileAlertPopover'
import { AlertFrequencyType, ProfileAlert } from '@modules/alerts/types'
import { P1PlusAccessConfigEvent } from '@modules/auth/hooks/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser } from '@modules/auth/types'
import shortlistApi from '@modules/shortlist/api'
import ShortlistTable from '@modules/shortlist/components/ShortlistTable'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import CreateIcon from '@mui/icons-material/Create'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import FilterIcon from '@mui/icons-material/FilterAlt'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import OutputOutlinedIcon from '@mui/icons-material/OutputOutlined'
import { CircularProgress } from '@mui/material'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Pagination from '@mui/material/Pagination'
import Typography from '@mui/material/Typography'
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useQueryParams } from '@shared/hooks/useQueryParams'
import { truncateWithDots } from '@shared/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { colors } from '@theme/variables'

import { UpdateManyMap } from './api/shortlist'
import AddNewShortlistSectionPopover from './components/AddNewShortlistSection/AddNewShortlistSectionPopover'
import EditCustomColumnPopover from './components/EditCustomColumnPopover'
import EditCustomColumnSectionPopover from './components/EditCustomColumnSection/EditCustomColumnSectionPopover'
import EditNotePopover from './components/EditNotePopover'
import EditShortlistsPopup from './components/EditShortlistsPopup'
import ImportProspectsPopup from './components/ImportProspectsPopup'
import { ImportProspectsQsFilterKey } from './components/ImportProspectsPopup/types'
import MoveProfilesPopup from './components/MoveProfilesPopup'
import { SearchField } from './components/SearchField'
import ShortlistFilterPopover from './components/ShortlistFilterPopover'
import CategoriesPopover from './components/ShortlistTable/CategoriesPopover'
import { StyledColumnsConfigButton, StyledFilterButtonButton } from './components/ShortlistTable/index.styles'
import { DEFAULT_SHORTLIST_PROFILES_PER_PAGE } from './constants'
import { useShortlistPageData, useShortlists } from './hooks/useShortlistMap'
import { DEFAULT_SHORTLIST_API_SORT, fieldNameToSortFieldMap, sortFieldsWithCompanyIds } from './hooks/useSortProfileValuesMap'
import {
  ActionItemsWrapper,
  EmptyProfilesInner,
  EmptyProfilesWrapper,
  EmptyShortlistButton,
  EmptyShortlistPicture,
  ManageColumnsText,
  ManageColumnsWrapper,
  ManageFiltersText,
  ManageFiltersWrapper,
  MoveSuccessNotification,
  ShortListTitleInner,
  ShortlistButtonsBox,
  ShortlistCounterContainer,
  ShortlistPagePaper,
  ShortlistPagePaperInner,
  ShortlistPageWrapper,
  ShortlistTableScrollbar,
  ShortlistTableScrollbarWrapper,
  ShortlistTitleContainer,
  ShortlistTitleDivider,
  StyledAddIconWrapper,
  TableNavigationBox,
  TabsWrapper,
  TextButton,
  TopTabsNavigationContainer,
} from './index.styles'
import {
  DEFAULT_SHORTLIST_SORT,
  ShortListCustomField,
  Shortlist,
  ShortlistCategoriesConfigProps,
  ShortlistColumnFilter,
  ShortlistFilterField,
  ShortlistItemProfile,
  ShortlistProfile,
  ShortlistProfilePriority,
  ShortlistSort,
  ShotlistDownload,
  ShotlistDownloadStatus,
} from './types'

const ShortlistPage = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const [query, setQuery] = useParamsFromQuery(
    {
      id: NumberParam,
      page: NumberParam,
      scrollY: NumberParam,
      scrollX: NumberParam,
    },
    {
      removeDefaultsFromUrl: true,
    },
  )

  const shortlistTableRef = useRef<Nullable<HTMLDivElement>>(null)
  const [activeShortlistId, setActiveShortlistId] = useState<number | undefined>()
  const [confirmDeleteShortlistId, setConfirmDeleteShortlistId] = useState<Nullable<number>>(null)
  const [isEditShortlistsPopupShown, setIsEditShortlistsPopupShown] = useState(false)
  const [isConfirmProfileDeletePopupShown, setIsConfirmProfileDeletePopupShown] = useState(false)
  const [isConfirmAlertsRemovePopupShown, setIsConfirmAlertsRemovePopupShown] = useState(false)
  const [confirmExitPopupCloseAction, setConfirmExitPopupCloseAction] = useState<Nullable<{ close: () => void }>>(null)
  const [selectProfilesNoticeAnchor, setSelectProfilesNoticeAnchor] =
    useState<Nullable<{ anchor: Nullable<HTMLElement>; text: string }>>(null)

  const [addShortlistPopoverAnchor, setAddShortlistPopoverAnchor] = useState<Nullable<HTMLElement>>(null)
  const [editCustomColumnPopoverAnchor, setEditCustomColumnPopoverAnchor] = useState<Nullable<HTMLElement>>(null)
  const [moveProfilesToShortlistAnchor, setMoveProfilesToShortlistAnchor] = useState<Nullable<HTMLElement>>(null)
  const [filterShortlistAnchor, setFilterShortlistAnchor] = useState<Nullable<HTMLElement>>(null)
  const [addAlertPopoverAnchor, setAddAlertPopoverAnchor] = useState<Nullable<HTMLElement>>(null)
  const [noteAnchor, setNoteAnchor] = useState<Nullable<HTMLElement>>(null)
  const [customColumnAnchor, setCustomColumnAnchor] = useState<Nullable<HTMLElement>>(null)
  const [profileNoteData, setProfileNoteData] = useState<Nullable<{ profileId?: number; note: string }>>(null)
  const [profileCustomColumnData, setProfileCustomColumnData] =
    useState<Nullable<{ profileId?: number; fieldId: number; customFieldValue: string }>>(null)
  const [showDuplicateError, setShowDuplicateError] = useState<boolean>(false)
  const [tablePage, setTablePage] = useState(1)
  const [dragProfileToShortlistAnchor, setDragProfileToShortlistAnchor] = useState<Nullable<HTMLElement>>(null)
  const [shortlistSort, setShortlistSort] = useState<ShortlistSort>(DEFAULT_SHORTLIST_SORT)
  const [importProspectsPopupVisible, setImportProspectsPopupVisible] = useState<boolean>(false)
  const [fetchDownloadStatus, setFetchDownloadStatus] = useState<boolean>(false)
  const [profileMoveSuccessMessage, setProfileMoveSuccessMessage] = useState<string | undefined>()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [customFieldSortId, setCustomFieldSortId] = useState<number>()
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300)
  const [shortlistFilters, setShortlistFilters] = useState<ShortlistColumnFilter[] | undefined>(undefined)

  const { user } = useAuthContext()
  const events =
    user?.events?.filter(
      userEvent =>
        userEvent.isRequestingEnabled &&
        userEvent.hasCompanyProfile &&
        (user.p1PlusAccessConfig?.events === P1PlusAccessConfigEvent.full || userEvent.isAttending),
    ) || []

  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)

  const {
    hasAccessToMultipleShortlists,
    hasAccessToAlerts,
    canDownloadShortlists,
    canCopyProspects,
    canCreateCustomColumns,
    canFilterShortlist,
  } = useFeatureFlags()
  const [isActionButtonsDisabled, setIsActionButtonsDisabled] = useState<boolean>(false)

  const {
    alertsShortlist,
    canAddShortlists,
    rawShortlists = [],
    shortlists,
    isUniqueName,
    isFetching,
    isInitialLoading,
  } = useShortlists()
  const [selectedProfileIds, setSelectedProfileIds] = useState<Array<number>>([])
  const [customColumnToEdit, setCustomColumnToEdit] = useState<ShortListCustomField | undefined>(undefined)
  const navigate = useNavigate()

  const { get, clearOneSilently } = useQueryParams()
  const eventIdFromQs = get(ImportProspectsQsFilterKey.event)

  const isEventFromQsExists = events.some(event => event.id === Number(eventIdFromQs))

  useEffect(() => {
    if (eventIdFromQs) {
      if (isEventFromQsExists) {
        setImportProspectsPopupVisible(true)
        return
      }

      clearOneSilently(ImportProspectsQsFilterKey.event)
    }
  }, [eventIdFromQs])

  const tabs = useMemo(
    () =>
      [...shortlists]
        .sort((shortlistA, shortlistB) => new Date(shortlistB.createdAt).getTime() - new Date(shortlistA.createdAt).getTime())
        .map(i => ({ id: i.id, label: i.name })),
    [shortlists],
  )

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const onClearSearch = () => {
    setSearchQuery('')
  }

  const shortlistIdFromQuery = useMemo(() => {
    if (query?.id && shortlists.some(i => i.id === query.id)) {
      return query?.id
    }

    return null
  }, [shortlists, query?.id])

  useEffect(() => {
    if (isFetching || !user?.id || !tabs?.length) {
      return
    }

    const shIdToSet = shortlistIdFromQuery ?? tabs[0]?.id

    if (!shIdToSet) {
      setQuery({ id: undefined })
      return
    }

    setActiveShortlistId(shIdToSet)
    setQuery({ id: shIdToSet, page: tablePage })
  }, [activeShortlistId, user?.id, tabs[0]?.id, shortlistIdFromQuery, tablePage, isFetching])

  const activeShortlist = useMemo(() => shortlists.find(i => i.id === activeShortlistId), [activeShortlistId, shortlists])

  const profiles = useMemo(() => activeShortlist?.profiles || [], [activeShortlist])
  useEffect(() => {
    setShortlistFilters(
      activeShortlist?.filters?.map(filter => ({
        ...filter,
        type: filter.attribute as ShortlistFilterField,
        attribute: (filter.nestedAttribute as number) || (filter.attribute! as ShortlistFilterField),
      })) || [],
    )
  }, [activeShortlist])

  const activeSortFieldName = useMemo(
    () =>
      (customFieldSortId
        ? customFieldSortId?.toString()
        : fieldNameToSortFieldMap[shortlistSort.fieldName as string] || shortlistSort.fieldName) as string,
    [shortlistSort],
  )
  const activeSort = useMemo(() => {
    if (activeSortFieldName) {
      const requestSort = {
        [activeSortFieldName]: shortlistSort.sortOrder!.toLowerCase(),
      }

      const updateSort = {
        field: activeSortFieldName,
        order: shortlistSort.sortOrder!.toLowerCase() as 'asc' | 'desc',
      }

      return { requestSort, updateSort }
    }

    return { requestSort: DEFAULT_SHORTLIST_API_SORT, updateSort: undefined }
  }, [shortlistSort])

  const { profilesPageData, totalProfilesAmount, isPageDataLoading } = useShortlistPageData({
    shortlistId: activeShortlistId,
    isEmptyShortlist: !profiles.length,
    from: Math.max(tablePage - 1, 0) * DEFAULT_SHORTLIST_PROFILES_PER_PAGE,
    sort: activeSort.requestSort as Record<string, 'asc' | 'desc'>,
    allowNotSortable: !!customFieldSortId,
    addCompanyIdsToRequest: sortFieldsWithCompanyIds[activeSortFieldName],
    searchQuery: debouncedSearchQuery,
    customFieldSortId,
    filters: shortlistFilters || [],
  })

  useEffect(() => {
    if (shortlistTableRef.current) {
      const shortlistTableMutationObserver = new MutationObserver(() => {
        if (query.scrollX && shortlistTableRef.current) {
          shortlistTableRef.current!.scrollTo({ left: query.scrollX })
          if (shortlistTableRef.current.scrollLeft === query.scrollX) {
            setQuery({ scrollX: undefined })
          }
        }

        if (query.scrollY) {
          window.scrollTo({ top: query.scrollY })
          if (window.scrollY === query.scrollY) {
            setQuery({ scrollY: undefined })
          }
        }
      })
      shortlistTableMutationObserver.observe(shortlistTableRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
      })
    }
  }, [shortlistTableRef.current])

  const { mutate: deleteShortlist } = useMutation({
    mutationFn: shortlistApi.deleteShortlist.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })
    },
  })

  const { mutate: downloadShortlist } = useMutation({
    mutationFn: shortlistApi.downloadShortlist.fn,
    onSuccess: () => {
      setFetchDownloadStatus(true)
    },
  })

  const { refetch: refetchShortlistDownload, data: shortlistDownloadData } = useQuery({
    queryKey: [shortlistApi.getDownloadShortlistStatus.key, activeShortlistId],
    queryFn: () => shortlistApi.getDownloadShortlistStatus.fn({ id: activeShortlistId }),

    enabled: fetchDownloadStatus && !!activeShortlistId,
    refetchInterval: fetchDownloadStatus ? 2000 : false,
    refetchIntervalInBackground: true,
  })

  useEffect(() => {
    if (shortlistDownloadData) {
      const { download } = shortlistDownloadData as {
        download: ShotlistDownload
      }
      if (download) {
        if (download.status === ShotlistDownloadStatus.SUCCESS) {
          setFetchDownloadStatus(false)
          const link = document.createElement('a')
          link.href = download.url
          link.click()
        }
        if (download.status === ShotlistDownloadStatus.IN_PROGRESS && !fetchDownloadStatus) {
          setFetchDownloadStatus(true)
        }
      }
    }
  }, [shortlistDownloadData])

  useEffect(() => {
    if (activeShortlistId) {
      refetchShortlistDownload()
    }
  }, [activeShortlistId])

  const { mutate: moveProfileBetweenShortlists } = useMutation({
    mutationFn: shortlistApi.moveProfileBetweenShortlists.fn,
    onSuccess: (_, { fromShortlistId, toShortlistIds, profileIds, copyProspects }) => {
      onSetMoveProfilesToShortlistAnchor(null)
      setDragProfileToShortlistAnchor(null)

      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })

      const pageAmount = totalProfilesAmount
        ? Math.ceil((totalProfilesAmount - profileIds.length) / DEFAULT_SHORTLIST_PROFILES_PER_PAGE)
        : 1
      setTablePage(Math.min(pageAmount, tablePage))

      queryClient.refetchQueries({ queryKey: [shortlistApi.getShortlistPageData.key, fromShortlistId] })

      toShortlistIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, id] })
      })

      // slight delay to let UI react first
      setTimeout(() => {
        setIsActionButtonsDisabled(false)
        setSelectedProfileIds(selectedProfileIds.filter(selectedProfileId => !profileIds.includes(selectedProfileId)))
      }, 1000)

      const SHORTLIST_ACTION_TRANSLATION_KEY = copyProspects
        ? translationKeys.PROSPECT_COPIED_TO_SHORTLIST
        : translationKeys.PROSPECT_MOVED_TO_SHORTLIST

      const prospectCountText = t(translationKeys.COUNT_PROSPECTS, { count: profileIds.length })

      const toShortlistText = t(SHORTLIST_ACTION_TRANSLATION_KEY, {
        targetShortlistName: shortlists.find(shortlist => shortlist.id === toShortlistIds[0])?.name,
        count: toShortlistIds.length,
      })

      const message = `${prospectCountText}&nbsp;${toShortlistText}`
      setProfileMoveSuccessMessage(message)
      setTimeout(() => setProfileMoveSuccessMessage(undefined), 5000)
    },
    onError: () => {
      setShowDuplicateError(true)
      setIsActionButtonsDisabled(false)
      setTimeout(() => setShowDuplicateError(false), 3000)
    },
  })

  const { mutate: bulkShortlistUpdate } = useMutation({
    mutationFn: shortlistApi.bulkShortlistUpdate.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })
    },
  })

  const { mutate: updateShortlist } = useMutation({
    mutationFn: shortlistApi.updateShortlistById.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })
    },
  })

  const { mutate: createShortlistCustomColumns } = useMutation({
    mutationFn: shortlistApi.createShortlistCustomColumn.fn,
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: [shortlistApi.getShortlists.key] })
      queryClient.refetchQueries({ queryKey: [shortlistApi.getShortlistPageData.key, activeShortlistId] })
    },
  })

  const { mutate: editShortlistCustomColumn } = useMutation({
    mutationFn: shortlistApi.editShortlistCustomColumn.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })
    },
  })

  const { mutate: createProfilesAlerts } = useMutation({
    mutationFn: alertsApi.createProfileAlert.fn,

    onSuccess: () => {
      setAddAlertPopoverAnchor(null)
      queryClient.invalidateQueries({ queryKey: [alertsApi.fetchProfilesAlerts.key] })
    },
  })

  const handleShortListsUpdate = useCallback((data: UpdateManyMap) => {
    bulkShortlistUpdate(data)
    setIsEditShortlistsPopupShown(false)
  }, [])

  const { mutate: rawUpdateShortlistProfileById } = useMutation({
    mutationFn: shortlistApi.updateShortlistProfileById.fn,
    onSuccess: async (_, { data }) => {
      setNoteEditAnchor(null)
      if (shortlistSort.fieldName! in data || shortlistFilters?.some(i => i.attribute in data)) {
        queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, activeShortlistId] })
      }

      queryClient.invalidateQueries({
        queryKey: [shortlistApi.getSortableFields.key, activeShortlistId],
      })
    },
  })

  const updateShortlistProfileById = useCallback(
    (updateData: { id: ShortlistProfile['id']; data: Partial<ShortlistProfile> }) => {
      // optimistically update cached data
      const matchingQueries = queryClient.getQueriesData<{ result: ShortlistProfile[] }>({
        queryKey: [shortlistApi.getShortlistPageData.key],
        exact: false,
      })

      matchingQueries.forEach(([queryKey, existingProfilesData]) => {
        if (!existingProfilesData?.result?.length) {
          return
        }

        const updatedProfiles = existingProfilesData.result.map(profile =>
          profile.id === updateData.id ? { ...profile, ...updateData.data } : profile,
        )

        queryClient.setQueryData(queryKey, { ...existingProfilesData, result: updatedProfiles })
      })

      // performing real update
      rawUpdateShortlistProfileById(updateData)
    },
    [],
  )

  const onEventSelectionPopoverClick = (event: Nullable<React.MouseEvent<HTMLElement>>) => {
    if (event?.currentTarget) {
      setImportProspectsPopupVisible(true)
    } else {
      setImportProspectsPopupVisible(false)
    }
  }

  const { mutate: deleteProfilesAlerts } = useMutation({
    mutationFn: alertsApi.deleteProfilesAlerts.fn,
    onSuccess: (_, variables) => {
      variables.forEach(deletedProfile => {
        queryClient.invalidateQueries({ queryKey: [alertsApi.fetchProfilesAlerts.key, deletedProfile.id] })
      })

      const pageAmount = totalProfilesAmount
        ? Math.ceil((totalProfilesAmount - selectedProfileIds.length) / DEFAULT_SHORTLIST_PROFILES_PER_PAGE)
        : 1
      setTablePage(Math.min(pageAmount, tablePage))
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, activeShortlistId] })

      setSelectedProfileIds([])
      setIsConfirmAlertsRemovePopupShown(false)
    },
  })

  const onDeleteProfiles = () => {
    if (!activeShortlistId) {
      return
    }

    const profilesToDelete = selectedProfileIds
      .map(id => profiles.find(i => i.id === id))
      .filter(Boolean) as ShortlistItemProfile[]

    updateShortlistProfiles({
      profiles: profilesToDelete,
      removeFromShortlistIds: [activeShortlistId],
    })
  }

  const onRemoveAlerts = () => {
    if (!activeShortlistId) {
      return
    }

    const profileAlertsToDelete = selectedProfileIds.map(id => {
      const profileToDelete = profiles.find(i => i.id === id)
      return { id: profileToDelete?.searchDocId, '@type': profileToDelete?.profileType }
    })

    deleteProfilesAlerts(profileAlertsToDelete)
  }

  const { mutate: updateShortlistProfiles } = useMutation({
    mutationFn: shortlistApi.updateShortlistProfiles.fn,
    onSuccess: (_, variables) => {
      const pageAmount = totalProfilesAmount
        ? Math.ceil((totalProfilesAmount - selectedProfileIds.length) / DEFAULT_SHORTLIST_PROFILES_PER_PAGE)
        : 1
      setTablePage(Math.min(pageAmount, tablePage))
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, activeShortlistId] })
      if (variables?.addToShortlistIds?.length) {
        variables.addToShortlistIds.forEach(shortlistId =>
          queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, shortlistId] }),
        )
      }

      setIsConfirmProfileDeletePopupShown(false)
      setMoveProfilesToShortlistAnchor(null)
      setIsActionButtonsDisabled(false)
      setSelectedProfileIds([])
    },
  })

  const onAddShortlistButtonClick = (event: Nullable<React.MouseEvent<HTMLElement>>) => {
    if (event?.currentTarget) {
      setAddShortlistPopoverAnchor(event.currentTarget)
    } else {
      setAddShortlistPopoverAnchor(null)
    }
  }

  const onAddCustomColumnButtonClick = (event: Nullable<React.MouseEvent<HTMLElement>>) => {
    if (event?.currentTarget) {
      setEditCustomColumnPopoverAnchor(event.currentTarget)
    } else {
      setEditCustomColumnPopoverAnchor(null)
    }
  }

  const onFilterShortlistButtonClick = (event: Nullable<React.MouseEvent<HTMLElement>>) => {
    if (searchQuery?.length) return
    if (event?.currentTarget) {
      setFilterShortlistAnchor(event.currentTarget)
    } else {
      setFilterShortlistAnchor(null)
    }
  }

  const onEditCustomColumnButtonClick = (event: Nullable<React.MouseEvent<HTMLElement>>, id: number) => {
    if (event?.currentTarget) {
      setEditCustomColumnPopoverAnchor(event.currentTarget)

      if (id) {
        setCustomColumnToEdit(activeShortlist?.customFields?.find(customColumn => customColumn.id === id))
      }
    } else {
      setEditCustomColumnPopoverAnchor(null)
    }
  }

  const onSetMoveProfilesToShortlistAnchor = (event: Nullable<React.MouseEvent<HTMLElement>>) => {
    if (event?.currentTarget) {
      if (selectedProfileIds?.length) {
        setMoveProfilesToShortlistAnchor(event.currentTarget)
      } else {
        setSelectProfilesNoticeAnchor({ anchor: event.currentTarget, text: translationKeys.YOU_NEED_TO_SELECT_TO_MOVE })
      }
    } else {
      setMoveProfilesToShortlistAnchor(null)
    }
  }

  const onSetAddAlertsToProfilesAnchor = (event: Nullable<React.MouseEvent<HTMLElement>>) => {
    if (event?.currentTarget) {
      if (selectedProfileIds?.length) {
        setAddAlertPopoverAnchor(event.currentTarget)
      } else {
        setSelectProfilesNoticeAnchor({
          anchor: event.currentTarget,
          text: translationKeys.YOU_NEED_TO_SELECT_PROFILES_TO_ADD_ALERTS,
        })
      }
    } else {
      setAddAlertPopoverAnchor(null)
    }
  }

  const handleCloseAddNewPopover = () => {
    setAddShortlistPopoverAnchor(null)
    return true
  }

  const handleCloseAddCustomColumnPopover = () => {
    setEditCustomColumnPopoverAnchor(null)
    setCustomColumnToEdit(undefined)
  }

  const handleCloseEditCustomColumnPopover = () => {
    setEditCustomColumnPopoverAnchor(null)
  }

  const handleSelectProfile = (profileIds: number | number[]) => {
    if (isArray(profileIds)) {
      if (!profileIds.length) {
        // unselecting only the page where we are
        setSelectedProfileIds(existingIds =>
          existingIds.filter(id => !profilesPageData.some(profile => profile.profileId === id)),
        )
      }

      setSelectedProfileIds(existingIds => [...Array.from(new Set([...profileIds, ...existingIds]))])
      return
    }

    setSelectedProfileIds(prevState => {
      if (prevState.includes(profileIds)) {
        return prevState.filter(i => i !== profileIds)
      }
      return [...prevState, profileIds]
    })
  }

  const handleClickProfile = () => {
    if (shortlistTableRef.current?.scrollLeft) {
      setQuery({ scrollX: shortlistTableRef.current?.scrollLeft }, 'pushIn')
    }

    if (window.scrollY) {
      setQuery({ scrollY: window.scrollY }, 'pushIn')
    }
  }

  const handleAddNewSuccess = (sh?: Shortlist) => {
    if (sh) {
      setAddShortlistPopoverAnchor(null)
      setActiveShortlistId(sh.id)
      navigate(`/plus/custom-list?id=${sh.id}&page=${tablePage}`)
    }
  }

  const handleCustomColumnEditConfirm = useCallback(
    (customField: ShortListCustomField, closePopup: boolean) => {
      if (closePopup) {
        setEditCustomColumnPopoverAnchor(null)
        setCustomColumnToEdit(undefined)
      } else {
        setCustomColumnToEdit(customField)
      }

      if (customField.id) {
        editShortlistCustomColumn({
          customField,
          shortlistId: activeShortlistId as number,
        })
      } else {
        createShortlistCustomColumns({
          customField,
          shortlistId: activeShortlistId as number,
        })
      }
    },
    [activeShortlistId],
  )

  useEffect(() => {
    setSelectedProfileIds([])
    setTablePage(query?.page || 1)
  }, [activeShortlistId, query?.page])

  const setNoteEditAnchor = (event: Nullable<React.MouseEvent<HTMLElement>>, data?: { profileId: number; note: string }) => {
    event?.stopPropagation()

    setProfileNoteData(data?.profileId ? data : null)

    if (event?.currentTarget) {
      setNoteAnchor(event.currentTarget)
    } else {
      setNoteAnchor(null)
    }
  }

  const setCustomColumnEditAnchor = (
    event: Nullable<React.MouseEvent<HTMLElement>>,
    data?: { profileId: number; fieldId: number; customFieldValue: string },
  ) => {
    event?.stopPropagation()

    setProfileCustomColumnData(data?.profileId ? data : null)

    if (event?.currentTarget) {
      setCustomColumnAnchor(event.currentTarget)
    } else {
      setCustomColumnAnchor(null)
    }
  }

  const onSetDragProfileAnchor = (event: Nullable<React.MouseEvent<HTMLElement>>) => {
    event?.stopPropagation()

    if (event?.currentTarget) {
      setDragProfileToShortlistAnchor(event.currentTarget)
    } else {
      setDragProfileToShortlistAnchor(null)
    }
  }

  const deleteNoteHandler = (deleteNodeCallback?: () => void) => {
    if (!profileNoteData?.note || !profileNoteData?.profileId) {
      return
    }

    updateShortlistProfileById({ id: profileNoteData?.profileId, data: { note: '' } })
    deleteNodeCallback?.()
  }

  const deleteCustomColumnHandler = (deleteNodeCallback?: () => void) => {
    if (!profileCustomColumnData?.customFieldValue || !profileCustomColumnData?.profileId || !profileCustomColumnData?.fieldId) {
      return
    }

    updateShortlistProfileById({
      id: profileCustomColumnData?.profileId,
      data: { customFieldValues: { [profileCustomColumnData?.fieldId]: profileCustomColumnData?.customFieldValue } },
    })
    deleteNodeCallback?.()
  }

  const updateNdaFieldHandler = (profileId: number, ndaFieldValue: boolean) => {
    updateShortlistProfileById({ id: profileId, data: { nda: ndaFieldValue } })
  }

  const updatePriorityFieldHandler = (profileId: number, priority: ShortlistProfilePriority) => {
    updateShortlistProfileById({ id: profileId, data: { priority } })
  }

  const updateTagIdFieldHandler = (profileId: number, tagIdFieldValue?: number | null) => {
    updateShortlistProfileById({ id: profileId, data: { tagId: tagIdFieldValue } })
  }

  const handleProfileDrop = useCallback(
    (fromShortlistId: number, toShortlistId: number, droppedItem: unknown, dropTarget: Nullable<HTMLElement>) => {
      setDragProfileToShortlistAnchor(dropTarget)

      setIsActionButtonsDisabled(true)

      moveProfileBetweenShortlists({
        fromShortlistId,
        toShortlistIds: [toShortlistId],
        profileIds: [(droppedItem as { profileId: number }).profileId],
        throwOnDuplicates: true,
      })
    },
    [],
  )

  const handleSaveAlertFrequency = useCallback(
    (frequency?: AlertFrequencyType) => {
      const profileAlerts: ProfileAlert[] = selectedProfileIds
        .map(id => {
          const profile = profiles.find(i => i.id === id)
          return (profile ? { id: profile.searchDocId, type: profile.profileType } : undefined) as ProfileAlert
        })
        .filter(profileAlert => !!profileAlert)
      const alertFrequency = frequency === AlertFrequencyType.NO_NOTIFICATION ? undefined : frequency
      createProfilesAlerts({ profileAlerts, alertFrequency })
      setAddAlertPopoverAnchor(null)
    },
    [createProfilesAlerts, selectedProfileIds],
  )

  const handleSaveCancel = useCallback(() => {
    setAddAlertPopoverAnchor(null)
  }, [])

  const totalPages = useMemo(() => {
    return totalProfilesAmount ? Math.ceil(totalProfilesAmount / DEFAULT_SHORTLIST_PROFILES_PER_PAGE) : 0
  }, [profilesPageData])

  const shouldShowPagination = useMemo(() => {
    return totalPages > 1 && !!profiles.length
  }, [profiles, totalPages])

  const onShortlistPageChange = useCallback(
    (event: React.ChangeEvent<unknown> | null, page: number, preventUrlUpdate?: boolean) => {
      setTablePage(page)
      if (!preventUrlUpdate) {
        navigate(`/plus/custom-list?id=${query.id}&page=${page}`)
      }
      if (window.scrollY > 250) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    [profilesPageData],
  )

  const handleConfirmMoveProfilesClick = (toShortlistIds: Shortlist['id'][], copyProspects?: boolean) => {
    if (!activeShortlistId) {
      return
    }

    setIsActionButtonsDisabled(true)

    moveProfileBetweenShortlists({
      toShortlistIds,
      profileIds: selectedProfileIds,
      fromShortlistId: activeShortlistId,
      copyProspects,
    })
  }
  const handleSortingChange = useCallback((sort: ShortlistSort) => {
    setShortlistSort(sort)
    onShortlistPageChange(null, 1, true)
  }, [])

  useEffect(() => {
    if (activeShortlistId && shortlistSort) {
      const existingShortlists = queryClient.getQueryData<Shortlist[]>([shortlistApi.getShortlists.key, user?.id]) || []
      const shortlistIndexToUpdate = existingShortlists.findIndex(shortlist => shortlist.id === activeShortlistId)
      if (shortlistIndexToUpdate !== -1) {
        const newShortlists = [
          ...existingShortlists.slice(0, shortlistIndexToUpdate),
          {
            ...existingShortlists[shortlistIndexToUpdate],
            name: activeShortlist?.name || '',
            sort: activeSort.updateSort,
          },
          ...existingShortlists.slice(shortlistIndexToUpdate + 1),
        ]
        queryClient.setQueryData([shortlistApi.getShortlists.key, user?.id], newShortlists)
      } else {
        queryClient.invalidateQueries({
          queryKey: [shortlistApi.getShortlists.key, user?.id],
        })
      }
    }
  }, [activeShortlistId, activeShortlist?.name, shortlistSort, updateShortlist])

  const handleShortlistFilterChange = useCallback(
    (newFilters: ShortlistColumnFilter[]) => {
      if (newFilters === shortlistFilters) return

      setShortlistFilters(newFilters)
      onShortlistPageChange(null, 1, true)
    },
    [activeShortlistId, shortlistFilters],
  )

  const updateConfig = useCallback(
    (config: ShortlistCategoriesConfigProps) => {
      if (activeShortlistId) {
        updateTableConfig({ id: activeShortlistId, config })
      }
    },
    [activeShortlistId],
  )

  const { mutate: updateTableConfig } = useMutation({
    mutationFn: shortlistApi.updateCategoriesConfig.fn,
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: [shortlistApi.getShortlists.key] })
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, activeShortlistId] })
    },
  })

  const addCustomColumnButton = useMemo(() => {
    return (
      <ManageColumnsWrapper $width="180px">
        <ManageColumnsText>{t(translationKeys.ADD_CUSTOM_COLUMN)}</ManageColumnsText>
        <StyledColumnsConfigButton data-testid="add-custom-column-button" onClick={onAddCustomColumnButtonClick}>
          <AddIcon fontSize="small" />
        </StyledColumnsConfigButton>
      </ManageColumnsWrapper>
    )
  }, [])

  const filterShortlistsButton = useMemo(() => {
    const filterButtonText = shortlistFilters?.length
      ? t(translationKeys.CUSTOM_LIST_FILTER_APPLIED, { count: shortlistFilters?.length })
      : t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_BTN)
    const filtersApplied = !!shortlistFilters?.length
    return (
      <ManageFiltersWrapper $filtersApplied={filtersApplied}>
        <ManageFiltersText $filtersApplied={filtersApplied}>{filterButtonText}</ManageFiltersText>
        <StyledFilterButtonButton
          $filtersApplied={filtersApplied}
          $disabled={!!searchQuery.length}
          data-testid="filter-shortlist-button"
          onClick={onFilterShortlistButtonClick}
        >
          <FilterIcon htmlColor={filtersApplied ? colors.primary : colors.white} fontSize="small" />
        </StyledFilterButtonButton>
      </ManageFiltersWrapper>
    )
  }, [shortlistFilters, searchQuery])

  const closeProfileMoveMessage = useCallback(() => {
    setProfileMoveSuccessMessage(undefined)
  }, [])

  const moveProspectsLabelKey = useMemo(
    () => (canCopyProspects ? translationKeys.MOVE_OR_COPY_TO_CUSTOM_LIST : translationKeys.MOVE_TO_DIFFERENT_CUSTOM_LIST),
    [canCopyProspects],
  )

  const closeFiltersPopup = useCallback(() => {
    onFilterShortlistButtonClick(null)
    updateShortlist({
      shortlistId: activeShortlistId!,
      params: {
        name: activeShortlist!.name,
        filters: shortlistFilters?.map(filter => ({
          ...filter,
          attribute: filter.type,
          combinator: filter.combinator || undefined,
        })),
      },
    })
  }, [[activeShortlistId, shortlistFilters]])

  const removeAlertsTexts = useMemo(() => {
    if (selectedProfileIds?.length > 1) {
      return {
        title: t(translationKeys.DELETE_ALERTS_FROM_LIST_TITLE),
        text: t(translationKeys.DELETE_ALERTS_FROM_LIST_SUBTITLE),
      }
    }
    return { title: t(translationKeys.DELETE_ALERT_FROM_LIST_TITLE), text: t(translationKeys.DELETE_ALERT_FROM_LIST_SUBTITLE) }
  }, [t, selectedProfileIds?.length])

  return (
    <ShortlistPageWrapper>
      {!isInitialLoading && (
        <>
          <TopTabsNavigationContainer>
            <TabsWrapper>
              <TabsCarousel
                tabs={tabs}
                value={activeShortlistId || tabs[0]?.id || false}
                onChange={(id: number) => {
                  setActiveShortlistId(id)
                  setTablePage(1)
                  navigate(`/plus/custom-list?id=${id}`)
                }}
                acceptDrops="profile"
                handleDrop={handleProfileDrop}
              />
            </TabsWrapper>

            <ActionItemsWrapper>
              {canAddShortlists && (
                <Button
                  variant="text"
                  color="primary"
                  startIcon={
                    <StyledAddIconWrapper>
                      <AddIcon color="inherit" sx={{ marginTop: '-10px', marginLeft: '-10px', fontSize: '32px' }} />
                    </StyledAddIconWrapper>
                  }
                  onClick={onAddShortlistButtonClick}
                >
                  {t(translationKeys.ADD_YOUR_CUSTOM_LIST)}
                </Button>
              )}
              {rawShortlists?.length > 0 && (
                <Button
                  variant="text"
                  color="primary"
                  startIcon={<CreateIcon color="inherit" sx={{ fontSize: '16px !important', marginRight: '-2px' }} />}
                  onClick={() => setIsEditShortlistsPopupShown(true)}
                >
                  {t(translationKeys.EDIT_CUSTOM_LISTS)}
                </Button>
              )}
              {events.length > 0 && (
                <Button
                  variant="text"
                  color="primary"
                  startIcon={<OutputOutlinedIcon color="inherit" sx={{ fontSize: '32px' }} />}
                  onClick={onEventSelectionPopoverClick}
                  sx={{ maxWidth: '200px' }}
                >
                  {t(translationKeys.IMPORT_PROSPECTS_FROM)}
                </Button>
              )}
            </ActionItemsWrapper>
          </TopTabsNavigationContainer>
          {shouldShowPagination && (
            <TableNavigationBox>
              <Pagination count={totalPages} page={tablePage} onChange={onShortlistPageChange} />
            </TableNavigationBox>
          )}
          <ShortlistPagePaper>
            <ShortlistPagePaperInner>
              {!!profiles?.length && (
                <ShortlistTitleContainer>
                  <Typography variant="h2">{truncateWithDots(activeShortlist?.name ?? '', 120)}</Typography>
                  <ShortlistCounterContainer>
                    <Typography variant="body1">{profiles.length} Items</Typography>
                  </ShortlistCounterContainer>
                  {profileMoveSuccessMessage && (
                    <MoveSuccessNotification>
                      {parse(profileMoveSuccessMessage)}
                      <IconButton onClick={closeProfileMoveMessage}>
                        <CloseIcon color="inherit" fontSize="inherit" />
                      </IconButton>
                    </MoveSuccessNotification>
                  )}
                </ShortlistTitleContainer>
              )}

              {!!rawShortlists?.length && !!profiles.length && (
                <ShortlistTitleContainer>
                  <ShortListTitleInner>
                    {rawShortlists?.length > 1 && hasAccessToMultipleShortlists && (
                      <>
                        <TextButton
                          variant="text"
                          color="primary"
                          endIcon={<DriveFileMoveOutlinedIcon color="inherit" fontSize="small" sx={{ marginLeft: '-2px' }} />}
                          onClick={onSetMoveProfilesToShortlistAnchor}
                          disabled={isActionButtonsDisabled}
                        >
                          {t(moveProspectsLabelKey)}
                        </TextButton>
                        <ShortlistTitleDivider />
                      </>
                    )}
                    <TextButton
                      variant="text"
                      color="primary"
                      endIcon={<DeleteOutlineOutlinedIcon color="inherit" fontSize="small" sx={{ marginLeft: '-6px' }} />}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        if (selectedProfileIds.length) {
                          setIsConfirmProfileDeletePopupShown(true)
                        } else {
                          setSelectProfilesNoticeAnchor({
                            anchor: e.currentTarget,
                            text: translationKeys.YOU_NEED_TO_SELECT_ALERTS_TO_DELETE,
                          })
                        }
                      }}
                      disabled={isActionButtonsDisabled}
                    >
                      {t(translationKeys.DELETE_CUSTOM_LIST)}
                    </TextButton>
                    {canDownloadShortlists && (
                      <>
                        <ShortlistTitleDivider />
                        <TextButton
                          variant="text"
                          color="primary"
                          endIcon={
                            fetchDownloadStatus ? (
                              <CircularProgress size="14px" />
                            ) : (
                              <DownloadIcon color="inherit" fontSize="small" sx={{ marginLeft: '-2px' }} />
                            )
                          }
                          onClick={() =>
                            downloadShortlist({ id: activeShortlistId, date: DateTime.now().toFormat('yyyy-MM-dd_HH_mm') })
                          }
                          disabled={isActionButtonsDisabled}
                        >
                          {t(translationKeys.DOWNLOAD_CUSTOM_LIST)}
                        </TextButton>
                        <ShortlistTitleDivider />
                      </>
                    )}
                    {hasAccessToAlerts && (
                      <TextButton
                        variant="text"
                        color="primary"
                        endIcon={<NotificationsNoneOutlinedIcon color="inherit" fontSize="small" sx={{ marginLeft: '-6px' }} />}
                        onClick={onSetAddAlertsToProfilesAnchor}
                      >
                        {t(translationKeys.ADD_ALERTS_BUTTON)}
                      </TextButton>
                    )}
                  </ShortListTitleInner>
                  <ShortlistButtonsBox>
                    <SearchField
                      searchQuery={searchQuery}
                      onSearchChange={handleSearchChange}
                      onClearSearch={onClearSearch}
                      disabled={!!shortlistFilters?.length}
                    />
                    {canFilterShortlist && filterShortlistsButton}
                    {canCreateCustomColumns && addCustomColumnButton}
                    {user?.p1PlusAccessConfig?.optionalShortlistColumns && (
                      <CategoriesPopover
                        updateTableConfig={updateConfig}
                        ndaFieldInitialValue={activeShortlist?.columns?.nda || { visible: false, width: 0 }}
                        noteFieldInitialValue={activeShortlist?.columns?.note || { visible: false, width: 0 }}
                        tagIdFieldInitialValue={activeShortlist?.columns?.tagId || { visible: false, width: 0 }}
                        priorityInitialValue={activeShortlist?.columns?.priority || { visible: false, width: 0 }}
                        dateAddedFieldInitialValue={activeShortlist?.columns?.createdAt || { visible: false, width: 0 }}
                        companyFieldInitialValue={activeShortlist?.columns?.company || { visible: false, width: 0 }}
                        lastEventFieldInitialValue={activeShortlist?.columns?.lastEvent || { visible: false, width: 0 }}
                        futureEventsFieldInitialValue={activeShortlist?.columns?.futureEvents || { visible: false, width: 0 }}
                        contactStatusFieldInitialValue={activeShortlist?.columns?.contactStatus || { visible: false, width: 0 }}
                        updatedFieldInitialValue={activeShortlist?.columns?.updatedAt || { visible: false, width: 0 }}
                        phaseFieldInitialValue={activeShortlist?.columns?.phase || { visible: false, width: 0 }}
                        optionalColumnsConfig={activeShortlist?.columns}
                        activeShortlist={activeShortlist}
                        onUpsertCustomColumnClick={onAddCustomColumnButtonClick}
                      />
                    )}
                  </ShortlistButtonsBox>
                </ShortlistTitleContainer>
              )}

              {!!profiles.length && (
                <ShortlistTable
                  activeShortlistId={activeShortlistId as number}
                  profilesPageData={profilesPageData}
                  totalProfilesAmount={totalProfilesAmount}
                  isPageDataLoading={isPageDataLoading}
                  optionalColumnsConfig={activeShortlist?.columns}
                  selectedRowIds={selectedProfileIds}
                  onSelectProfile={handleSelectProfile}
                  onClickProfile={handleClickProfile}
                  onEditNote={setNoteEditAnchor}
                  onEditCustomColumn={setCustomColumnEditAnchor}
                  onChangeNda={updateNdaFieldHandler}
                  onChangePriority={updatePriorityFieldHandler}
                  onTagUpdate={updateTagIdFieldHandler}
                  sort={shortlistSort}
                  onSortChange={handleSortingChange}
                  alertsShortlistId={alertsShortlist?.id}
                  ref={shortlistTableRef}
                  activeShortlist={activeShortlist}
                  handleEditCustomColumnSuccess={handleCustomColumnEditConfirm}
                  handleCloseEditCustomColumnPopover={handleCloseEditCustomColumnPopover}
                  onEditCustomColumnButtonClick={onEditCustomColumnButtonClick}
                  setCustomFieldSortId={setCustomFieldSortId}
                  customFieldSortId={customFieldSortId}
                  updateConfig={updateConfig}
                />
              )}
              {!profiles?.length && (
                <EmptyProfilesWrapper>
                  <EmptyProfilesInner>
                    <Typography variant="h2">
                      {isEcommerce
                        ? t(translationKeys.EMPTY_ECOMMERCE_CUSTOM_LISTS_TITLE)
                        : t(translationKeys.EMPTY_CUSTOM_LISTS_TITLE)}
                    </Typography>
                    <EmptyShortlistPicture src={BlankStateImage} alt="blank state image" />
                    <EmptyShortlistButton onClick={() => navigate('/plus/search')}>
                      {t(translationKeys.EMPTY_CUSTOM_LISTS_BTN)}
                    </EmptyShortlistButton>
                  </EmptyProfilesInner>
                </EmptyProfilesWrapper>
              )}
            </ShortlistPagePaperInner>
          </ShortlistPagePaper>
          {shouldShowPagination && (
            <TableNavigationBox>
              <Pagination count={totalPages} page={tablePage} onChange={onShortlistPageChange} />
            </TableNavigationBox>
          )}
        </>
      )}
      {isEditShortlistsPopupShown && (
        <EditShortlistsPopup
          isOpen
          onCancel={() => setIsEditShortlistsPopupShown(false)}
          onConfirm={handleShortListsUpdate}
          onDelete={setConfirmDeleteShortlistId}
          shouldClose={(value: Record<string, unknown>) => {
            if (value && JSON.stringify(value) !== '{}') {
              setConfirmExitPopupCloseAction({ close: () => setIsEditShortlistsPopupShown(false) })
              return false
            }

            return true
          }}
          shortlists={rawShortlists}
        />
      )}
      <NoticePopover
        id={selectProfilesNoticeAnchor ? 'select_profiles_notice_popover' : undefined}
        anchor={selectProfilesNoticeAnchor?.anchor || null}
        onClose={() => setSelectProfilesNoticeAnchor(null)}
        title={t(translationKeys.NO_ITEM_SELECTED)}
        text={t(selectProfilesNoticeAnchor?.text as string)}
      />
      <AddNewShortlistSectionPopover
        anchor={addShortlistPopoverAnchor}
        isUniqueName={isUniqueName}
        shortlists={shortlists}
        onCancel={handleCloseAddNewPopover}
        onSuccess={handleAddNewSuccess}
      />
      {editCustomColumnPopoverAnchor && (
        <EditCustomColumnSectionPopover
          anchor={editCustomColumnPopoverAnchor}
          onCancel={handleCloseAddCustomColumnPopover}
          activeShortlist={activeShortlist}
          onSave={handleCustomColumnEditConfirm}
          customColumn={customColumnToEdit}
        />
      )}
      {filterShortlistAnchor && (
        <ShortlistFilterPopover
          anchor={filterShortlistAnchor}
          handleClose={closeFiltersPopup}
          activeShortlist={activeShortlist}
          existingFilters={shortlistFilters}
          filtersUpdated={filters => handleShortlistFilterChange(filters)}
        />
      )}
      {!!confirmDeleteShortlistId && (
        <ConfirmPopup
          id="shortlist-confirm-delete-shortlist"
          isOpen
          width={260}
          title={t(translationKeys.DELETE_A_CUSTOM_LIST)}
          text={t(translationKeys.ARE_YOU_SURE_TO_DELETE_A_CUSTOM_LIST)}
          cancelBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CANCEL_BTN)}
          confirmBtnText={t(translationKeys.DELETE_CUSTOM_LIST)}
          onCancel={() => {
            setIsEditShortlistsPopupShown(true)
            setConfirmDeleteShortlistId(null)
          }}
          onConfirm={() => {
            deleteShortlist({ id: confirmDeleteShortlistId })
            setConfirmDeleteShortlistId(null)

            if (rawShortlists.length === 1) {
              setIsEditShortlistsPopupShown(false)
            }
          }}
        />
      )}
      {!!isConfirmAlertsRemovePopupShown && (
        <ConfirmPopup
          id="shortlist-confirm-permanently-delete-profile-shortlist"
          isOpen
          width={225}
          title={removeAlertsTexts.title}
          text={removeAlertsTexts.text}
          cancelBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CANCEL_BTN)}
          confirmBtnText={t(translationKeys.REMOVE)}
          onCancel={() => {
            setIsConfirmAlertsRemovePopupShown(false)
          }}
          onConfirm={onRemoveAlerts}
        />
      )}
      {!!isConfirmProfileDeletePopupShown && (
        <ConfirmPopup
          id="shortlist-confirm-remove-alerts-from-profiles"
          isOpen
          width={225}
          title={t(translationKeys.DELETE_ITEMS)}
          text={t(translationKeys.DO_YO_WANT_PERMANENTLY_DELETE)}
          cancelBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CANCEL_BTN)}
          confirmBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CONFIRM_BTN)}
          onCancel={() => {
            setIsConfirmProfileDeletePopupShown(false)
          }}
          onConfirm={onDeleteProfiles}
        />
      )}
      {addAlertPopoverAnchor && (
        <ProfileAlertPopover
          anchor={addAlertPopoverAnchor}
          onSave={handleSaveAlertFrequency}
          onCancel={handleSaveCancel}
          isEdit={false}
        />
      )}
      {moveProfilesToShortlistAnchor && (
        <MoveProfilesPopup
          shortlists={rawShortlists.filter(i => i.id !== activeShortlistId)}
          anchor={moveProfilesToShortlistAnchor}
          setAnchor={setMoveProfilesToShortlistAnchor}
          onConfirmClick={handleConfirmMoveProfilesClick}
          canCopy={canCopyProspects}
          shouldClose={(changes: unknown) => {
            if (!Object.keys(changes as object).length) {
              return true
            }

            setConfirmExitPopupCloseAction({ close: () => onSetMoveProfilesToShortlistAnchor(null) })
            return false
          }}
        />
      )}
      {noteAnchor && (
        <EditNotePopover
          setAnchor={setNoteEditAnchor}
          anchor={noteAnchor}
          profileNoteData={profileNoteData}
          deleteNoteHandler={deleteNoteHandler}
          setConfirmExitPopupCloseAction={setConfirmExitPopupCloseAction}
          updateNote={updateShortlistProfileById}
        />
      )}
      {customColumnAnchor && (
        <EditCustomColumnPopover
          setAnchor={setCustomColumnEditAnchor}
          anchor={customColumnAnchor}
          profileCustomColumnData={profileCustomColumnData}
          deleteCustomColumnHandler={deleteCustomColumnHandler}
          setConfirmExitPopupCloseAction={setConfirmExitPopupCloseAction}
          updateCustomColumn={updateShortlistProfileById}
        />
      )}
      {importProspectsPopupVisible && (
        <ImportProspectsPopup
          isVisible={importProspectsPopupVisible}
          setIsVisible={setImportProspectsPopupVisible}
          events={events}
          enablePreselect={isEventFromQsExists}
          selectShortlist={setActiveShortlistId}
        />
      )}

      {showDuplicateError && (
        <ErrorPopup
          title={t(translationKeys.DUPLICATE_ERROR_TITLE)}
          text={t(translationKeys.DUPLICATE_ERROR_TEXT)}
          onClose={() => setShowDuplicateError(false)}
          width={320}
          anchor={dragProfileToShortlistAnchor}
          setAnchor={onSetDragProfileAnchor as ErrorPopupProps['setAnchor']}
        />
      )}
      {confirmExitPopupCloseAction && (
        <ConfirmPopup
          id="shortlist-page-confirm-exit-popup"
          width={294}
          contentWidth={180}
          isOpen
          title={t(translationKeys.UNSAVED_SETTINGS)}
          text={t(translationKeys.YOU_HAVENOT_SAVED_CHANGES)}
          cancelBtnText={t(translationKeys.CONTINUE_EDITING)}
          confirmBtnText={t(translationKeys.CLOSE_ANYWAY)}
          onCancel={() => setConfirmExitPopupCloseAction(null)}
          onConfirm={() => {
            confirmExitPopupCloseAction?.close?.()
            setConfirmExitPopupCloseAction(null)
          }}
        />
      )}

      {!isPageDataLoading && shortlistTableRef.current && (
        <ShortlistTableScrollbarWrapper>
          <ShortlistTableScrollbar>
            <CustomScrollbar contentRef={shortlistTableRef} />
          </ShortlistTableScrollbar>
        </ShortlistTableScrollbarWrapper>
      )}
    </ShortlistPageWrapper>
  )
}
export default ShortlistPage
